import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Timestamp } from "firebase/firestore";
import { Persona } from "@um/contracts";
import { StoreApi } from "zustand";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Timestamp formatting utilities
export function formatTimestamp(
  timestamp: Timestamp | Date | string | undefined,
  options?: {
    dateOnly?: boolean;
    timeOnly?: boolean;
    relative?: boolean;
  }
): string {
  if (!timestamp) return "N/A";

  let date: Date;

  if (timestamp instanceof Timestamp) {
    date = timestamp.toDate();
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else if (typeof timestamp === "string") {
    date = new Date(timestamp);
  } else {
    return "N/A";
  }

  if (options?.relative) {
    return formatRelativeTime(date);
  }

  if (options?.dateOnly) {
    return date.toLocaleDateString();
  }

  if (options?.timeOnly) {
    return date.toLocaleTimeString();
  }

  return date.toLocaleString();
}

export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  // For anything older than a week, show the actual date
  return date.toLocaleDateString();
}

export function calculateAgeFromTimestamp(
  dateOfBirth: Timestamp | undefined
): number {
  if (!dateOfBirth) return 0;
  const today = new Date();
  const birthDate = dateOfBirth.toDate();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
}

export function calculateAgeFromPersona(persona: Persona | undefined): number {
  if (!persona) return 0;

  // First try to use dateOfBirth if available
  if (persona.dateOfBirth) {
    return calculateAgeFromTimestamp(persona.dateOfBirth);
  }

  // Fallback to age string if available
  if (persona.age) {
    const ageNum = parseInt(persona.age);
    return isNaN(ageNum) ? 0 : ageNum;
  }

  return 0;
}

export const storeLinks = {
  ios: "https://apps.apple.com/us/app/unique-minds/id6743822738?itscg=30200&itsct=apps_box_badge&mttnsubad=6743822738",
  android: "https://play.google.com/store/apps/details?id=com.uniqueminds.app",
};

/**
 * Resolve once `predicate(state)` holds, checking synchronously first.
 *
 * @param timeoutMs when set, reject (and unsubscribe) if the predicate has
 *   not held within this many ms. Without it the promise waits forever and
 *   leaks its subscription — fine for callers that are guaranteed to settle,
 *   but not for anything on a user-facing startup path.
 */
export function waitForState<T>(
  store: StoreApi<T>,
  predicate: (state: T) => boolean,
  timeoutMs?: number
): Promise<T> {
  // complete syncronously if possible
  const state = store.getState();
  if (predicate(state)) {
    return Promise.resolve(state);
  }

  // subscribe for changes
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const unsubscribe = store.subscribe((state) => {
      if (predicate(state)) {
        if (timer !== undefined) clearTimeout(timer);
        unsubscribe();
        resolve(state);
      }
    });

    if (timeoutMs !== undefined) {
      timer = setTimeout(() => {
        unsubscribe();
        reject(new Error(`waitForState timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    }
  });
}
